/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  CreateMediaFolderRequest,
  DeleteAdminV1OrgMediaFoldersIdPathParameters,
  DeleteAdminV1OrgMediaIdPathParameters,
  GetAdminV1OrgMedia200,
  GetAdminV1OrgMediaFoldersIdPathParameters,
  GetAdminV1OrgMediaIdPathParameters,
  GetAdminV1OrgMediaParams,
  ImportMediaRequest,
  Media,
  MediaFolder,
  MediaFolderItemsRequest,
  MediaFolderList,
  PatchAdminV1OrgMediaFoldersIdPathParameters,
  PatchAdminV1OrgMediaIdBody,
  PatchAdminV1OrgMediaIdPathParameters,
  PostAdminV1OrgMediaFoldersIdItemsPathParameters,
  PostAdminV1OrgMediaFoldersIdItemsRemovePathParameters,
  PostAdminV1OrgMediaIdConfirmPathParameters,
  PostAdminV1OrgMediaPresignBody,
  PresignMediaResponse,
  PutAdminV1OrgMediaUploadsTokenPathParameters,
  UpdateMediaFolderRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Newest first unless sort says otherwise. q matches the file name or alt text, missingAlt=true keeps files without alt text, mimeType and kind narrow the type, folderId keeps one folder's files and unfiled=true the files in no folder. createdAt_gte and createdAt_lte keep files uploaded on or between those UTC days, both inclusive
 * @summary Media library of the organization
 */
export const getAdminV1OrgMedia = (
    params?: GetAdminV1OrgMediaParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgMedia200>>,) => {
      return customInstance<GetAdminV1OrgMedia200>(
      {url: `/admin/v1/org/media`, method: 'GET',
        params
    },
      options);
    }
  /**
 * PUT the file to uploadUrl with the returned headers, then call confirm. The upload URL is valid for 15 minutes and accepts exactly one upload
 * @summary Start an upload: returns where to PUT the bytes
 */
export const postAdminV1OrgMediaPresign = (
    postAdminV1OrgMediaPresignBody: BodyType<PostAdminV1OrgMediaPresignBody>,
 options?: SecondParameter<typeof customInstance<PresignMediaResponse>>,) => {
      return customInstance<PresignMediaResponse>(
      {url: `/admin/v1/org/media/presign`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1OrgMediaPresignBody
    },
      options);
    }
  /**
 * The API downloads the file itself (public hosts only, images only, up to the upload limit), dedupes it by checksum like a confirmed upload and queues the renditions. 422 when the URL is not an image, 413 over the limit, 502 when the host does not answer
 * @summary Import an image from a public URL
 */
export const postAdminV1OrgMediaImport = (
    importMediaRequest: BodyType<ImportMediaRequest>,
 options?: SecondParameter<typeof customInstance<Media>>,) => {
      return customInstance<Media>(
      {url: `/admin/v1/org/media/import`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: importMediaRequest
    },
      options);
    }
  /**
 * Authenticated by the one-time token in the URL, so the browser can PUT with progress and no session
 * @summary Upload target for the local storage driver
 */
export const putAdminV1OrgMediaUploadsToken = (
    { token }: PutAdminV1OrgMediaUploadsTokenPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/media/uploads/${token}`, method: 'PUT'
    },
      options);
    }
  /**
 * Checks the bytes against the declared type, dedupes by checksum (returns the existing record), records dimensions and queues WebP renditions for images. Idempotent
 * @summary Finish an upload: verifies the file and records it
 */
export const postAdminV1OrgMediaIdConfirm = (
    { id }: PostAdminV1OrgMediaIdConfirmPathParameters,
 options?: SecondParameter<typeof customInstance<Media>>,) => {
      return customInstance<Media>(
      {url: `/admin/v1/org/media/${id}/confirm`, method: 'POST'
    },
      options);
    }
  /**
 * @summary Get a media record
 */
export const getAdminV1OrgMediaId = (
    { id }: GetAdminV1OrgMediaIdPathParameters,
 options?: SecondParameter<typeof customInstance<Media>>,) => {
      return customInstance<Media>(
      {url: `/admin/v1/org/media/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * folderIds replaces the file's folders; 422 INVALID_REFERENCE with meta.folderIds for ids that are not the organization's
 * @summary Update alt text, metadata or the folders the file sits in
 */
export const patchAdminV1OrgMediaId = (
    { id }: PatchAdminV1OrgMediaIdPathParameters,
    patchAdminV1OrgMediaIdBody: BodyType<PatchAdminV1OrgMediaIdBody>,
 options?: SecondParameter<typeof customInstance<Media>>,) => {
      return customInstance<Media>(
      {url: `/admin/v1/org/media/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1OrgMediaIdBody
    },
      options);
    }
  /**
 * 409 RESOURCE_IN_USE with meta.usedBy (product_media, product_featured, variant, content_block, collection, store_product_featured, blog_post_featured, swatch, rich_text) while anything references it
 * @summary Delete a media record and its files
 */
export const deleteAdminV1OrgMediaId = (
    { id }: DeleteAdminV1OrgMediaIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/media/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * The whole list, by name, with the number of files in each. A file can sit in any number of folders; filter the media list with folderId or unfiled=true
 * @summary Media folders of the organization
 */
export const getAdminV1OrgMediaFolders = (

 options?: SecondParameter<typeof customInstance<MediaFolderList>>,) => {
      return customInstance<MediaFolderList>(
      {url: `/admin/v1/org/media/folders`, method: 'GET'
    },
      options);
    }
  /**
 * 409 ALREADY_EXISTS when the name is taken (case-insensitive); 422 LIMIT_EXCEEDED past 200 folders
 * @summary Create a media folder
 */
export const postAdminV1OrgMediaFolders = (
    createMediaFolderRequest: BodyType<CreateMediaFolderRequest>,
 options?: SecondParameter<typeof customInstance<MediaFolder>>,) => {
      return customInstance<MediaFolder>(
      {url: `/admin/v1/org/media/folders`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createMediaFolderRequest
    },
      options);
    }
  /**
 * @summary Get a media folder
 */
export const getAdminV1OrgMediaFoldersId = (
    { id }: GetAdminV1OrgMediaFoldersIdPathParameters,
 options?: SecondParameter<typeof customInstance<MediaFolder>>,) => {
      return customInstance<MediaFolder>(
      {url: `/admin/v1/org/media/folders/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Rename a media folder
 */
export const patchAdminV1OrgMediaFoldersId = (
    { id }: PatchAdminV1OrgMediaFoldersIdPathParameters,
    updateMediaFolderRequest: BodyType<UpdateMediaFolderRequest>,
 options?: SecondParameter<typeof customInstance<MediaFolder>>,) => {
      return customInstance<MediaFolder>(
      {url: `/admin/v1/org/media/folders/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateMediaFolderRequest
    },
      options);
    }
  /**
 * Removes the folder and its links only; the files stay in the library
 * @summary Delete a media folder
 */
export const deleteAdminV1OrgMediaFoldersId = (
    { id }: DeleteAdminV1OrgMediaFoldersIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/media/folders/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Files already in the folder are left as they are. 422 INVALID_REFERENCE with meta.mediaIds for ids that are not the organization's
 * @summary Add files to a media folder
 */
export const postAdminV1OrgMediaFoldersIdItems = (
    { id }: PostAdminV1OrgMediaFoldersIdItemsPathParameters,
    mediaFolderItemsRequest: BodyType<MediaFolderItemsRequest>,
 options?: SecondParameter<typeof customInstance<MediaFolder>>,) => {
      return customInstance<MediaFolder>(
      {url: `/admin/v1/org/media/folders/${id}/items`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: mediaFolderItemsRequest
    },
      options);
    }
  /**
 * Ids that are not in the folder are ignored; the files stay in the library
 * @summary Remove files from a media folder
 */
export const postAdminV1OrgMediaFoldersIdItemsRemove = (
    { id }: PostAdminV1OrgMediaFoldersIdItemsRemovePathParameters,
    mediaFolderItemsRequest: BodyType<MediaFolderItemsRequest>,
 options?: SecondParameter<typeof customInstance<MediaFolder>>,) => {
      return customInstance<MediaFolder>(
      {url: `/admin/v1/org/media/folders/${id}/items/remove`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: mediaFolderItemsRequest
    },
      options);
    }
  export type GetAdminV1OrgMediaResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgMedia>>>
export type PostAdminV1OrgMediaPresignResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgMediaPresign>>>
export type PostAdminV1OrgMediaImportResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgMediaImport>>>
export type PutAdminV1OrgMediaUploadsTokenResult = NonNullable<Awaited<ReturnType<typeof putAdminV1OrgMediaUploadsToken>>>
export type PostAdminV1OrgMediaIdConfirmResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgMediaIdConfirm>>>
export type GetAdminV1OrgMediaIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgMediaId>>>
export type PatchAdminV1OrgMediaIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgMediaId>>>
export type DeleteAdminV1OrgMediaIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgMediaId>>>
export type GetAdminV1OrgMediaFoldersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgMediaFolders>>>
export type PostAdminV1OrgMediaFoldersResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgMediaFolders>>>
export type GetAdminV1OrgMediaFoldersIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgMediaFoldersId>>>
export type PatchAdminV1OrgMediaFoldersIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgMediaFoldersId>>>
export type DeleteAdminV1OrgMediaFoldersIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgMediaFoldersId>>>
export type PostAdminV1OrgMediaFoldersIdItemsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgMediaFoldersIdItems>>>
export type PostAdminV1OrgMediaFoldersIdItemsRemoveResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgMediaFoldersIdItemsRemove>>>
